// ── An answer, as BLOCKS (chat Figma rebuild, section 40011407:276544) ──────────────────────────
//
// The chat frames draw an answer as more than prose: a card of tasks under a sentence (frame 31),
// a thread (29), a draft email (07), "Task created · …" (08), a table of possible connections (19).
// `v1/intelligent/ask` grows an optional, ordered `blocks[]` for that (PLAN §2, the answer-payload
// contract); `answer` stays the plain-text fallback, and everything that only knows `answer` — an
// older BFF, a failed turn, a restored session — still reads as one text block.
//
// Pure on purpose: the store normalises every response through here, and the renderer registry
// (components/Home/blocks/registry.js) only ever sees what this returns.

/** The block types the UI knows how to draw. Anything else is shown as its text. */
export const BLOCK_TYPES = Object.freeze([
  'text', 'list', 'thread', 'emailDraft', 'entityCreated', 'suggestion', 'connections', 'webAnswer',
])

const isKnown = (type) => BLOCK_TYPES.includes(type)
const str = (v) => (v === null || v === undefined ? '' : String(v))
const lines = (parts) => parts.map(str).filter(Boolean).join('\n')

/** One listing row as a line of text: an email by who and what, an entity by its title. */
const rowText = (row) => {
  if (!row || typeof row !== 'object') return ''
  if (row.kind === 'email') return [str(row.from), str(row.subject)].filter(Boolean).join(' — ')
  return str(row.title)
}

// Plain text per block type — what Copy answer puts on the clipboard, and what a block whose
// renderer is not built yet shows in its place (`fallbackText`).
const TEXT_OF = {
  text: (b) => str(b.markdown),
  list: (b) => (Array.isArray(b.sections) ? b.sections : [])
    .map((s) => lines([s?.label, ...(Array.isArray(s?.rows) ? s.rows : []).map((r) => `- ${rowText(r)}`)]))
    .filter(Boolean)
    .join('\n\n'),
  thread: (b) => lines((Array.isArray(b.messages) ? b.messages : [])
    .map((m) => [str(m?.from), str(m?.subject), str(m?.snippet)].filter(Boolean).join(' — '))),
  emailDraft: (b) => lines([b.subject ? `Subject: ${b.subject}` : '', b.body ? `\n${b.body}` : '']),
  entityCreated: (b) => `${capitalise(b.kind || 'task')} created · ${str(b.title)}`,
  suggestion: (b) => lines([`Suggested ${str(b.kind || 'task')}`, b.title]),
  connections: (b) => lines([b.title, ...(Array.isArray(b.rows) ? b.rows : [])
    .map((r) => `- ${[str(r?.title), str(r?.reason)].filter(Boolean).join(' — ')}`)]),
  // The web half of a mixed answer (busy-ai-srv#26): the engine's text as written, then its
  // sources — what Copy answer puts on the clipboard for that part.
  webAnswer: (b) => lines([str(b.markdown), ...(Array.isArray(b.sources) ? b.sources : [])
    .map((s) => (s && s.url ? `- ${[str(s.title), str(s.url)].filter(Boolean).join(' — ')}` : ''))]),
}

/** "task" → "Task". Only ever called with a non-empty kind (`entityCreated` defaults it). */
function capitalise(s) {
  const t = str(s)
  return t[0].toUpperCase() + t.slice(1)
}

/**
 * A block as plain text.
 *
 * Known types are read field by field; an unknown one falls back to whatever text it carries
 * (`fallbackText`, `markdown`, `text`). Never throws, never returns anything but a string.
 */
export function blockText(block) {
  if (!block || typeof block !== 'object') return ''
  const of = TEXT_OF[block.type]
  if (of) return of(block)
  return str(block.fallbackText || block.markdown || block.text)
}

/**
 * The response's blocks, ready to render.
 *
 *   • `data.blocks` is used when it is a non-empty array; otherwise the answer is one text block
 *     (and no answer at all is no blocks — the turn draws nothing, exactly as before);
 *   • a block of a type the UI does not know becomes a TEXT block of its own text, so a newer
 *     server can never put a hole in an answer;
 *   • non-objects are dropped;
 *   • every block gets a stable `key` (the server's own, else its position) — what
 *     `timelineSearch/patchBlock` finds it by — and a `fallbackText`, which is what a stub
 *     renderer shows until the package that owns its type replaces it.
 */
export function normalizeBlocks(data) {
  const raw = Array.isArray(data?.blocks) && data.blocks.length
    ? data.blocks
    : [{ type: 'text', markdown: str(data?.answer) }]
  return raw
    .map((block, i) => {
      if (!block || typeof block !== 'object') return null
      const key = block.key ? String(block.key) : `b${i}`
      const fallbackText = blockText(block)
      if (!isKnown(block.type)) return { type: 'text', key, markdown: fallbackText, fallbackText, originalType: str(block.type) }
      return { ...block, key, fallbackText }
    })
    .filter((b) => b && (b.type !== 'text' || b.markdown))
}

/** The whole answer as text: every block's text, a blank line between. */
export function blocksText(blocks) {
  return (Array.isArray(blocks) ? blocks : []).map(blockText).filter(Boolean).join('\n\n')
}

export default normalizeBlocks
